import {identity, isEmpty, isNil, values} from "lodash";
import numeral from "numeral";
import {
  CUSTOM_DISTRIBUTION_METHOD_STEP,
  deliveryEnum,
  FEDERAL_WITHOLDINGS,
  PROPORTIONATE_DISTRIBUTION_METHOD_STEP,
  selectableDays,
  selectableMonths,
  STATE_CODES,
} from "../settings/settings";
import {checkForWithholdingState, multiHoldingState} from "./fund-utils";
import {getDisabledDays} from "./date-utils";
import {isDate} from "lodash/lang";

/**
 * Returns the first value that is not undefined, null, or NaN. If every value is one of these, then
 * the last value is returned.
 *
 * @param {*} values Values to test and first non-undefined/null/NaN value is returned
 */
export function coalesce(...values) {
  const len = values.length;
  if (len === 0) {
    return null;
  }
  for (let i = 0; i < len; i++) {
    const value = values[i];
    if (
      value !== undefined &&
      value !== null &&
      !(typeof value === "number" && isNaN(value))
    ) {
      return value;
    }
  }
  return values[len - 1];
}

export function isMocks() {
  if (!isLocalhost()) {
    return false;
  }
  const queryString = window.location.search;
  return queryString ? queryString.replace("?", "").includes("mocks") : false;
}

export function isLocalhost() {
  return (
    window && window.location.hostname.toLowerCase().indexOf("localhost") !== -1
  );
}

export function sortStatesList(object, key) {
  object.sort((a, b) => {
    const temp1 = a[key].toLowerCase();
    const temp2 = b[key].toLowerCase();
    if (temp1 < temp2) {
      return -1;
    }
    if (temp1 > temp2) return 1;
    return 0;
  });
  return object;
}

/**
 * Given an array, returns a lookup map for each item in the array, where the key is the key provided
 * by the key picker callback or the item itself if no callback is provided.
 *
 * @param array
 * @param keyPicker
 * @param itemConverter
 * @return {{}}
 */
export function toLookup(
  array,
  keyPicker = identity,
  itemConverter = identity
) {
  const lookup = {};
  if (Array.isArray(array)) {
    array.forEach((item) => (lookup[keyPicker(item)] = itemConverter(item)));
  }
  return lookup;
}

/**
 * function to map boolean values to 'Y/N' because analytics can't support
 *  @param {Boolean} bool
 *  @return {string} true/"Y" or false/"N"
 */
export function boolToYesNo(bool) {
  if (isNil(bool)) {
    return "N";
  }
  return bool ? "Y" : "N";
}

export function currency(value) {
  return numeral(value).format("$0,0.00");
}

export function getMonthObject(value) {
  return selectableMonths.filter((val) => val.value === value);
}

export function getDayObject(value) {
  return selectableDays.filter((val) => val.value === value);
}

function createMapByAgreementID(userFunds) {
  if (typeof userFunds !== "object" || userFunds === null) {
    return {};
  }
  const agreementMap = {};
  // object should have retirement and non-retirement groupings
  if (Object.keys(userFunds)) {
    Object.keys(userFunds).forEach((grouping) => {
      if (
        grouping.indexOf("Agreements") !== -1 &&
        grouping.indexOf("college") === -1
      ) {
        // each grouping has agreement types defined underneath (TRADITIONAL IRA, JOINT-TENANT etc)
        Object.keys(userFunds[grouping]).forEach((agreementTypeKey) => {
          if (userFunds[grouping][agreementTypeKey]) {
            userFunds[grouping][agreementTypeKey].forEach((fund) => {
              if (fund && fund.agreementID) {
                agreementMap[fund.agreementID] = fund;
              }
            });
          }
        });
      }
    });
  }
  return agreementMap;
}

export function parseExistingRMD(
  rmdAgreements,
  userFunds,
  nonRetirementFunds,
  userBanks,
  statePercentLookUp,
  rolloverLockedFunds,
  traditionalLockedFunds,
  enableDistributionMethod
) {
  /*const {
    withdrawDayNumber,
    withdrawMonthText,
    rmdTypeCode,
  } = payload;*/
  const rmdAgreementIds = rmdAgreements.map(
    (fund) => `${fund.sourceAgreementKey.agreementID}`
  );
  const lockedFundIds = traditionalLockedFunds.map((fund) => fund.agreementID).concat(rolloverLockedFunds.map((fund) => fund.agreementID))
  const funds = {
    traditionalFunds: userFunds.TRADITIONAL_IRA.filter(
      (fund) => rmdAgreementIds.indexOf(`${fund.agreementID}`) > -1 ||
        (!(rmdAgreements[0].rmdType.toLowerCase() === "disproportionate" && enableDistributionMethod) &&
        !lockedFundIds.includes(fund.agreementID))
    ).map((fund) => {
      return {
        ...fund,
        value:
          rmdAgreements[rmdAgreementIds.indexOf(`${fund.agreementID}`)]
            ?.percentage,
      };
    }),
    rolloverFunds: userFunds.ROLLOVER_IRA.filter(
      (fund) => rmdAgreementIds.indexOf(`${fund.agreementID}`) > -1 ||
        (!(rmdAgreements[0].rmdType.toLowerCase() === "disproportionate" && enableDistributionMethod) &&
        !lockedFundIds.includes(fund.agreementID))
    ).map((fund) => {
      return {
        ...fund,
        value:
          rmdAgreements[rmdAgreementIds.indexOf(`${fund.agreementID}`)]
            ?.percentage,
      };
    }),
  };
  const allSelectedFunds = (
    funds.traditionalFunds ? values(funds.traditionalFunds) : []
  ).concat(funds.rolloverFunds ? values(funds.rolloverFunds) : []);
  const multiHoldingStates = multiHoldingState(
    allSelectedFunds,
    statePercentLookUp,
    STATE_CODES
  );
  const withHoldingState = checkForWithholdingState(
    STATE_CODES,
    allSelectedFunds
  );
  let percentage;
  let mandatory;
  let multiState;
  if (multiHoldingStates && multiHoldingStates.multiState) {
    multiState = multiHoldingStates.multiState;
    mandatory = multiHoldingStates.mandatory;
  } else {
    if (withHoldingState) {
      const stateCode = withHoldingState.stateCode;
      if (statePercentLookUp[stateCode]) {
        percentage = statePercentLookUp[stateCode].percent;
        mandatory = statePercentLookUp[stateCode].mandatory;
      }
    }
  }
  const selectedBank = userBanks.filter((bank) =>
    bank.bankAgreementID === rmdAgreements[0].bankAgreementKey.bankAgreementID
      ? rmdAgreements[0].bankAgreementKey.bankAgreementID
      : 0
  );
  const selectedMonths = rmdAgreements[0].transfers
    .filter((monthObj) => monthObj.status.enumerationID === 10)
    .map((month) => getMonthObject(month.month)[0]);
  const disabledDays = getDisabledDays(selectedMonths);
  let selectedDays = getDayObject(rmdAgreements[0].transferDays[0]);
  let isValidDay = rmdAgreements[0].transferDays.length <=  1;
  if (isValidDay) {
    selectedDays.forEach((day) => {
      if (disabledDays.includes(day.value)) {
        isValidDay = false;
        selectedDays = [];
      }
    })
  } else {
    selectedDays = [];
  }
  const isBank = selectedBank.length > 0 && selectedBank[0].bankStatus === null &&  rmdAgreements[0]?.deliveryType?.enumerationName === 'ElectronicTransferToBank';
  const isMail = rmdAgreements[0]?.deliveryType?.enumerationName === 'CheckToAddress';
   const isCardLocked =
     rolloverLockedFunds.length > 0 || traditionalLockedFunds.length > 0;
  return {
    selectedDestination: rmdAgreements[0].type.enumerationID === 20
      ? deliveryEnum['InKindTransfer']
      : isBank
      ? deliveryEnum[rmdAgreements[0].deliveryType.enumerationName]
      : isMail
      ? deliveryEnum[rmdAgreements[0].deliveryType.enumerationName]
      : null,
    selectedBank: selectedBank.length > 0 && selectedBank[0].bankStatus === null? selectedBank[0] : {},
    selectedAddress: {},
    selectedReinvestment: {
      fund: createMapByAgreementID(nonRetirementFunds)[
        `${rmdAgreements[0].targetAgreementKey.agreementID}`
      ],
    },
    selectedMonths: selectedMonths, //getMonthObject(month)[0]),
    selectedDay: selectedDays,
    invalidDay: !isValidDay,
    selectedDistributionMethod: {
      type:
        isCardLocked
        ? null
        : rmdAgreements[0].rmdType.toLowerCase() === "disproportionate" && enableDistributionMethod
          ? CUSTOM_DISTRIBUTION_METHOD_STEP
          : PROPORTIONATE_DISTRIBUTION_METHOD_STEP,
      funds: funds,
    },
    taxWithholding: {
      selected:
        FEDERAL_WITHOLDINGS[
          rmdAgreements[0].IRAWithholding > 0
            ? "ELECT_FEDERAL_WITHHOLDING"
            : "DECLINE_FEDERAL_WITHHOLDING"
        ],
      value:
        rmdAgreements[0].IRAWithholding > 0
          ? rmdAgreements[0].IRAWithholding
          : null,
      mandatory: mandatory,
      multiState: multiState,
      stateList: multiHoldingStates.stateList,
      percentage: percentage,
      withHoldingState: withHoldingState,
    },
    estimateDate: "",
  };
}

export const getQueryStringValue = (key) => {
  return decodeURIComponent(
    window.location.search.replace(
      new RegExp(
        "^(?:.*[&\\?]" +
          encodeURIComponent(key).replace(/\s+/g, "\\$&") +
          "(?:\\=([^&]*))?)?" +
          ".*$",
        "i"
      ),
      "$1"
    )
  );
};

export const isMobileCookieSet = () => {
  const temp = document.cookie
    .split('; ')
    .find((row) => row.startsWith('additionalParams='));
  return (
    temp?.replace(
      new RegExp(
        '^(?:.*[&\\?]' +
          encodeURIComponent('mobile').replace(/\s+/g, '\\$&') +
          '(?:\\=([^&]*))?)?' +
          '.*$',
        'i'
      ),
      '$1'
    ) === '1'
  );
};

export const compareEventDateToCurrentDate = (futureDate) => {
  const eventDate = new Date(futureDate).getTime();
  const currentDate = new Date().getTime();

  //if the event date hasn't happened yet return false
  if(eventDate > currentDate) {
    return false;
  } else {
    return true;
  }
}

export const hasValue = (value) => {
  switch (typeof value) {
    case 'string':
      return value !== '';

    case 'number':
      return !isNaN(value);

    case 'boolean':
    case 'function':
      return true;

    default:
      return isDate(value) ? !isNaN(value.getTime()) : !isEmpty(value);
  }
}

export function getHouseHoldInformation(householdBalanceTierStatus) {
  const householdId = householdBalanceTierStatus;;
    const getSCSTier =
      householdId === 'Personal Services' ||
      householdId === 'Enhanced Personal Services' ||
      householdId === 'Select Services'
          ? true
          : false;
  return getSCSTier;
}

export function stringPropSorter(getter) {
  return function stringPropSorterImpl(x, y) {
    if (x === y) {
      return 0;
    }
    const xn = isNil(x);
    const yn = isNil(y);
    if (xn && yn) {
      return 0;
    }
    if (xn) {
      return 1;
    }
    if (yn) {
      return -1;
    }
    const xVal = getter(x);
    const yVal = getter(y);
    const xStr = typeof xVal === 'string';
    const yStr = typeof yVal === 'string';
    if (xStr && yStr) {
      return xVal.localeCompare(yVal);
    }
    if (xStr) {
      return -1;
    }
    if (yStr) {
      return 1;
    }
    return 0;
  };
}

export const isPromise = (value) => !!value && typeof value.then === 'function';
